import React, { useEffect, useState } from "react";
import { useRouter } from "next/router";
import DefaultErrorPage from "next/error";
import { format } from "date-fns";
import { BASE_CATEGORY_SLUG } from "lib/api";
import { getStatus } from "lib/utils";
import config from "../../config";
import {
  useEvents,
  useCategories,
  getUniqueChildCategories,
  useSessionStorage,
} from "hooks";

import Arrow from "../../components/Icons/Arrow";
import CloseModalIcon from "../../components/Icons/CloseModalIcon";
import LocationIcon from "../../components/Icons/LocationIcon";
import Banner from "../../components/banners/churchBanner";
import List from "../../components/churchPage/list";
import Head from "next/head";

const MapIframe = ({ location }) => {
  return (
    <iframe
      width="100%"
      height="465"
      style={{
        border: "0",
        marginTop: "2rem",
        borderRadius: "10px",
      }}
      loading="lazy"
      src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyD8V3ZgTYmqnr9lQ_ilEkQndVunTxZW7sE&q=${location}&language=fi`}
    />
  );
};

const ChurchContent = ({ slug, renderError }) => {
  const router = useRouter();
  const prevPath = useSessionStorage("prevPath");

  if (renderError) {
    return (
      <>
        <Head>
          <meta name="robots" content="noindex" />
        </Head>
        <DefaultErrorPage statusCode={renderError} />
      </>
    );
  }

  const { data: churches, error: churchesError } = useCategories(
    BASE_CATEGORY_SLUG,
    {
      accountUrls: slug,
      include: "children",
    },
    {
      enabled: !!slug,
    }
  );

  const allCategorySlugs: any | string[] =
    churches && getUniqueChildCategories(churches);

  const slugs = allCategorySlugs && allCategorySlugs.map((cs) => cs.slug);

  const { data: recommendedEvents } = useEvents(
    ["suosittelemme"],
    {
      accountUrls: slug as string,
    },
    { enabled: !!slugs }
  );

  const { data: preliveEvents } = useEvents(
    slugs,
    {
      accountUrls: slug as string,
      states: [3],
      startDate: format(new Date(), "yyyy-MM-dd"),
    },
    { enabled: !!slugs }
  );

  const { data: onDemandEvents } = useEvents(
    slugs,
    {
      accountUrls: slug as string,
      states: [1],
      endDate: format(new Date(), "yyyy-MM-dd"),
    },
    { enabled: !!slugs }
  );

  const { data: liveEvents } = useEvents(
    slugs,
    {
      accountUrls: slug as string,
      states: [0],
    },
    { enabled: !!slugs }
  );

  if (churchesError) {
    return (
      <>
        <Head>
          <meta name="robots" content="noindex" />
        </Head>
        <DefaultErrorPage statusCode={getStatus(churchesError)} />
      </>
    );
  }

  return (
    <div id="ModalBody" className="min-h-screen">
      {churches?.[0] && (
        <div className="w-full h-full g-1 px-10 pt-8 phone:pt-4">
          <Head>
            <title key="title">
              {churches[0]?.account?.name} ─ Virtuaalikirkko
            </title>
            <meta
              name="title"
              key="m-title"
              content={`${churches[0].account?.name} ─ Virtuaalikirkko`}
            />
            <meta
              property="og:url"
              key="og:url"
              content={`${config?.domain}/kirkko/${slug}`}
            />
            <meta
              property="og:title"
              key="og:title"
              content={`${churches[0].account?.name} ─ Virtuaalikirkko`}
            />
            <meta
              name="description"
              key="description"
              content="Sivulta löydät kirkon tiedot sekä kaikki lähetykset ja tallenteet"
            />
            <meta
              property="og:description"
              key="og:description"
              content="Sivulta löydät kirkon tiedot sekä kaikki lähetykset ja tallenteet"
            />
            <meta
              property="og:image"
              key="og:image"
              content={churches[0].images.background || "/images/2021.png"}
            />
          </Head>
          <div
            className="absolute top-0 left-0 right-0 h-[425px] bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: `url(${
                churches[0].images.background || "/images/2021.png"
              })`,
            }}
          />
          <div className="w-full flex justify-end z-[1] relative phone:ml-6">
            <button
              className="bg-blue text-black py-2.5 px-7 rounded-full text-bodyText flex items-center phone-200:p-2"
              onClick={() => (prevPath ? router.back() : router.push("/"))}
            >
              <CloseModalIcon className="phone-200:w-[8px] phone-200:h-[8px]" />
              <span className="pl-2.5 phone-200:hidden"> Sulje</span>
            </button>
          </div>
          <Banner church={churches[0]} />
          {recommendedEvents?.events?.length > 0 && (
            <section className="flex justify-center items-center pb-16 tablet:pb-0">
              <List
                itemNb={5}
                events={recommendedEvents.events}
                link={"/kategoria/suosittelemme"}
                header={"SUOSITTELEMME"}
              />
            </section>
          )}

          {liveEvents?.events?.length > 0 && (
            <section className="flex justify-center items-center pb-16 tablet:pb-0">
              <List
                itemNb={5}
                events={liveEvents.events}
                link={"/suorat-lahetykset"}
                header={"SUORAT LÄHETYKSET"}
              />
            </section>
          )}

          {preliveEvents?.events?.length > 0 && (
            <section className="flex justify-center items-center pb-16 tablet:pb-0">
              <List
                itemNb={5}
                events={preliveEvents.events}
                link={"/tulevat-lahetykset"}
                header={"TULEVAT LÄHETYKSET"}
                isUpcoming
              />
            </section>
          )}

          {onDemandEvents?.events?.length > 0 && (
            <section className="flex justify-center items-center pb-16 tablet:pb-0">
              <List
                itemNb={10}
                events={onDemandEvents.events}
                link={"/menneet-lahetykset"}
                header={"MENNEET LÄHETYKSET"}
              />
            </section>
          )}
          {liveEvents?.events?.length === 0 &&
            preliveEvents?.events?.length === 0 &&
            onDemandEvents?.events?.length === 0 && (
              <section className="py-16 max-w-churchPage mx-auto">
                <h2 className="font-bold text-2xl my-5">LÄHETYKSET</h2>
                <h3 className="text-xl">
                  Kirkko ei ole vielä julkaissut lähetyksiä.
                </h3>
              </section>
            )}
          {churches?.[0]?.descriptionParsed?.data?.address && (
            <section className="flex justify-center items-center pb-32">
              <div className="flex flex-col w-full max-w-churchPage">
                <h2 className="font-bold text-2xl my-5 phone-100:text-lg">
                  SIJAINTI
                </h2>
                <div className="flex justify-center items-center flex-wrap">
                  <div className="flex items-center mr-auto">
                    <LocationIcon height={26.7} width={20.03} />
                    <span className="ml-3">
                      {churches[0].descriptionParsed.data.address}
                    </span>
                  </div>
                  <a
                    target="_blank"
                    href={`https://google.com/maps?q=${churches[0].descriptionParsed.data.address}`}
                    className="flex items-center ml-auto"
                    style={{ color: "#7d7d7d" }}
                  >
                    <span className="mr-3">Katso Google Mapsissa</span>{" "}
                    <Arrow color={"#7d7d7d"} />
                  </a>
                </div>
                {churches[0].descriptionParsed.data.address && (
                  <div>
                    <MapIframe
                      location={churches[0].descriptionParsed.data.address}
                    />
                  </div>
                )}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
};

export default ChurchContent;
